import { useEffect, useState } from 'react';
import { addDoc, collection, getDocs, orderBy, query, serverTimestamp, where, updateDoc, doc } from 'firebase/firestore';

export const useSupportTickets = (auth, db, profile) => {
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [messageLoading, setMessageLoading] = useState(false);
  const [newTicketSubject, setNewTicketSubject] = useState('');
  const [newTicketBody, setNewTicketBody] = useState('');
  const [ticketStatus, setTicketStatus] = useState('');
  const [replyText, setReplyText] = useState('');

  const loadTickets = async (uid) => {
    if (!uid) return;
    setTicketsLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, 'tickets'), where('ownerUid', '==', uid), orderBy('updatedAt', 'desc'))
      );
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setTickets(list);
    } catch (err) {
      console.error('Failed to load tickets', err);
    } finally {
      setTicketsLoading(false);
    }
  };

  useEffect(() => {
    const user = auth?.currentUser;
    if (user) loadTickets(user.uid);
  }, [auth?.currentUser]);

  const createTicket = async (e) => {
    e.preventDefault();
    const user = auth?.currentUser;
    if (!user || !newTicketSubject.trim() || !newTicketBody.trim()) return;
    try {
      const ticketRef = await addDoc(collection(db, 'tickets'), {
        ownerUid: user.uid,
        teamId: profile?.teamId || null,
        subject: newTicketSubject.trim(),
        status: 'open',
        lastMessage: newTicketBody.trim(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await addDoc(collection(db, 'tickets', ticketRef.id, 'messages'), {
        senderUid: user.uid,
        text: newTicketBody.trim(),
        createdAt: serverTimestamp(),
      });
      setNewTicketSubject('');
      setNewTicketBody('');
      await loadTickets(user.uid);
    } catch (err) {
      console.error('Failed to create ticket', err);
    }
  };

  const openTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setMessageLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, 'tickets', ticket.id, 'messages'), orderBy('createdAt', 'asc'))
      );
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setTicketMessages(list);
      setTicketStatus(ticket.status || 'open');
    } catch (err) {
      console.error('Failed to load messages', err);
    } finally {
      setMessageLoading(false);
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    const user = auth?.currentUser;
    if (!user || !selectedTicket || !replyText.trim()) return;
    try {
      await addDoc(collection(db, 'tickets', selectedTicket.id, 'messages'), {
        senderUid: user.uid,
        text: replyText.trim(),
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'tickets', selectedTicket.id), {
        lastMessage: replyText.trim(),
        updatedAt: serverTimestamp(),
        status: ticketStatus || 'open',
      });
      setReplyText('');
      await openTicket(selectedTicket);
      await loadTickets(user.uid);
    } catch (err) {
      console.error('Failed to send reply', err);
    }
  };

  return {
    tickets,
    ticketsLoading,
    selectedTicket,
    ticketMessages,
    messageLoading,
    newTicketSubject,
    setNewTicketSubject,
    newTicketBody,
    setNewTicketBody,
    ticketStatus,
    setTicketStatus,
    replyText,
    setReplyText,
    createTicket,
    openTicket,
    sendReply,
    loadTickets,
  };
};

export default useSupportTickets;
